import React from 'react';
import { List, Datagrid, FunctionField, useListContext, useRecordContext } from 'react-admin';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { formatDateOnly, daysSince } from '../../Utils/date';
import {
	FAILED_PAYMENT_STATUSES,
	SubscriptionStatus,
	StripeLink,
} from '../Subscriptions/subscriptionShared';

const AccountCell: React.FC<{ label?: string }> = () => {
	const record = useRecordContext<any>();
	if (!record) return null;
	return (
		<Box>
			<Typography variant="body2" fontWeight={600}>
				{record.billingAccount?.name || record.billingAccountId}
			</Typography>
			<Typography variant="caption" color="text.secondary">
				{record.stripeSubscriptionId || `sub #${record.id}`}
			</Typography>
		</Box>
	);
};

// Le compteur vit dans la List : une requête de plus pour un seul entier ferait
// double emploi avec celle que la List lance déjà, au même filtre.
const FailedPaymentsHeader: React.FC = () => {
	const { total } = useListContext();
	return (
		<CardContent sx={{ pb: 0 }}>
			<Typography variant="h6">Paiements en échec ({total ?? 0})</Typography>
			<Typography variant="body2" color="text.secondary">
				L'ancienneté court depuis la fin de la période payée : c'est la seule date que laisse
				le webhook Stripe. Indépendant du filtre de période. Le montant dû se lit sur Stripe,
				en un clic.
			</Typography>
		</CardContent>
	);
};

export const FailedPaymentsBlock: React.FC = () => (
	<Card sx={{ mb: 3 }}>
		<List
			resource="subscriptions"
			disableSyncWithLocation
			storeKey="billingAlerts.failedPayments"
			component={Box}
			title={false}
			actions={false}
			exporter={false}
			// Filtre permanent : il l'emporte sur tout filtre utilisateur, l'écran ne
			// peut donc pas afficher autre chose que des impayés.
			filter={{ status: FAILED_PAYMENT_STATUSES }}
			perPage={25}
			// Fin de période la plus ancienne d'abord : le plus en retard en tête.
			sort={{ field: 'current_period_end', order: 'ASC' }}
			empty={
				<Typography sx={{ p: 2 }} color="text.secondary">
					Aucun paiement en échec.
				</Typography>
			}
		>
			<FailedPaymentsHeader />
			<Datagrid bulkActionButtons={false} rowClick={false}>
				<AccountCell label="Compte" />
				<FunctionField
					label="Formule"
					render={(record: any) => record.plan?.name || record.planId}
				/>
				<FunctionField
					label="Abonnement"
					render={(record: any) => <SubscriptionStatus status={record.status} />}
				/>
				<FunctionField
					label="Impayé depuis"
					sortBy="current_period_end"
					textAlign="right"
					render={(record: any) => {
						const days = daysSince(record.currentPeriodEnd);
						return (
							<Box>
								<Typography variant="body2">{days === null ? '—' : `${days} j`}</Typography>
								<Typography variant="caption" color="text.secondary">
									depuis le {formatDateOnly(record.currentPeriodEnd)}
								</Typography>
							</Box>
						);
					}}
				/>
				<FunctionField
					label="Stripe"
					render={(record: any) => (
						<StripeLink subId={record.stripeSubscriptionId} label="Ouvrir" />
					)}
				/>
			</Datagrid>
		</List>
	</Card>
);
